import axios from "axios";
import { toast } from "sonner";
import { enqueueMutation, cacheGet, readCache, flushQueue } from "@/offlineQueue";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  // Default timeout on all requests. Long enough for OCR/upload but not infinite.
  // Individual calls can still override (see admin/import-listino which sets 5min).
  timeout: 30000,
});

// Endpoints that must NEVER be queued offline (login, uploads, ocr).
const NEVER_QUEUE = [
  /\/auth\/login$/,
  /\/uploads$/,
  /\/ocr\//,
];

function shouldQueue(url = "", method = "get") {
  const m = String(method).toLowerCase();
  if (m === "get") return false;
  if (NEVER_QUEUE.some((r) => r.test(url))) return false;
  return true;
}

function cacheKey(config) {
  const u = config.url || "";
  return u.startsWith("http") ? u : `${API}${u.startsWith("/") ? "" : "/"}${u}`;
}

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("tf_token");
  const userName = localStorage.getItem("tf_user");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (userName) config.headers["X-User-Name"] = userName;
  return config;
});

client.interceptors.response.use(
  async (response) => {
    // Cache successful GETs so we can serve them offline later.
    try {
      if ((response.config.method || "get").toLowerCase() === "get") {
        await cacheGet(cacheKey(response.config), response.data);
      }
    } catch {
      // best-effort; do not block success path
    }
    return response;
  },
  async (error) => {
    const config = error.config || {};
    const method = (config.method || "get").toLowerCase();
    const isNetwork = !error.response && (error.code === "ERR_NETWORK" || error.message === "Network Error" || !navigator.onLine);
    const status = error.response?.status;

    // Auto-logout on 401 from any endpoint EXCEPT the login itself.
    // Handles expired / revoked JWTs cleanly instead of showing confusing errors.
    if (status === 401 && !/\/auth\/login$/.test(config.url || "")) {
      try {
        localStorage.removeItem("tf_token");
        localStorage.removeItem("tf_user");
        localStorage.removeItem("tf_role");
        localStorage.removeItem("tf_store_id");
      } catch { /* private mode */ }
      // Push the user to /login only if not already there, avoiding reload loops.
      if (typeof window !== "undefined" && !/\/login\b/.test(window.location.pathname)) {
        window.location.replace("/login");
      }
    }

    // Auto-retry once on transient network errors / 5xx for idempotent methods (GET/HEAD).
    // Guarded by _retryCount to avoid infinite loops.
    const isTransient = isNetwork || (typeof status === "number" && status >= 500 && status <= 599);
    if (isTransient && (method === "get" || method === "head") && navigator.onLine) {
      config._retryCount = (config._retryCount || 0) + 1;
      if (config._retryCount <= 1) {
        await new Promise((r) => setTimeout(r, 400));
        try {
          return await client.request(config);
        } catch {
          // fall through to normal handling
        }
      }
    }

    if (isNetwork && method === "get") {
      const cached = await readCache(cacheKey(config));
      if (cached !== null && cached !== undefined) {
        return { data: cached, status: 200, statusText: "OK (cache)", headers: {}, config, __fromCache: true };
      }
    }

    if (isNetwork && shouldQueue(config.url, method)) {
      await enqueueMutation({
        method,
        url: config.url,
        data: config.data ? (typeof config.data === "string" ? JSON.parse(config.data) : config.data) : null,
        headers: {
          "Content-Type": config.headers?.["Content-Type"] || "application/json",
        },
      });
      toast.success("Modifica salvata offline. Verrà sincronizzata al ritorno online.");
      return { data: { __queued: true }, status: 202, statusText: "Queued offline", headers: {}, config, __queued: true };
    }

    return Promise.reject(error);
  },
);

// Auto-flush queue when connection is restored.
let flushing = false;
async function tryFlush() {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  try {
    const { flushed, failed } = await flushQueue(client);
    if (flushed) toast.success(`Sincronizzate ${flushed} modifiche offline`);
    if (failed) toast.error(`${failed} modifiche non sincronizzabili (rifiutate dal server)`);
    if (flushed) window.dispatchEvent(new CustomEvent("offline-queue-flushed"));
  } finally {
    flushing = false;
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("online", tryFlush);
  // Try once on load, in case there's a leftover queue from a previous session.
  setTimeout(tryFlush, 1500);
}

export default client;

export const fileUrl = (fileId) => {
  const token = localStorage.getItem("tf_token");
  return `${API}/files/${fileId}?auth=${encodeURIComponent(token || "")}`;
};
